import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { useKit } from "@/context/KitContext";
import { useAuth } from "@/context/AuthContext";
import { useStoreData } from "@/hooks/useStoreData";
import { ProductCard } from "@/components/ProductCard";
import { KitPanel } from "@/components/KitPanel";

export default function Store() {
  const { user } = useAuth();
  const { products, settings } = useStoreData();
  const { items, total, sync } = useKit();

  useEffect(() => {
    if (products) sync(products);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  if (user.has_order) return <Navigate to="/my-order" replace />;

  return (
    <div className="space-y-12 pb-20 lg:pb-0">
      {items.length > 0 && (
        <Link to="/checkout" data-testid="mobile-kit-bar" className="lg:hidden fixed bottom-4 inset-x-4 z-40 h-14 px-5 rounded-full bg-slate-900 text-white flex items-center justify-between shadow-xl">
          <span className="text-sm font-semibold">{items.length}/{settings.max_items} items · {total} pts</span>
          <span className="flex items-center gap-1 text-sm">Checkout <ArrowRight className="w-4 h-4" /></span>
        </Link>
      )}
      <section className="animate-rise">
        <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Hi {user.name || user.email.split("@")[0]}</p>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 max-w-3xl">
          Pick {settings.max_items} pieces. <span className="text-[#1A73E8]">Make it yours.</span>
        </h1>
        <p className="mt-4 text-slate-600 max-w-xl">
          You have <span className="font-semibold text-slate-900" data-testid="user-points-budget">{user.points} points</span> to spend on one kit of up to {settings.max_items} items.
        </p>
      </section>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          {!products ? (
            <Loader2 className="w-6 h-6 animate-spin text-[#1A73E8]" />
          ) : (
            <div className="grid sm:grid-cols-2 gap-6" data-testid="product-grid">
              {products.map((p, i) => (
                <div key={p.id} className="animate-rise" style={{ animationDelay: `${i * 70}ms` }}>
                  <ProductCard p={p} maxItems={settings.max_items} budget={user.points} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-4">
          <KitPanel maxItems={settings.max_items} budget={user.points} />
        </div>
      </div>
    </div>
  );
}
